import { Link } from "react-router-dom";
import { Card } from "primereact/card";

/**
 * Reusable Mobile Cards Template
 * Based on the Orders mobile cards design for consistent look and feel
 * 
 * @param {Object} props - Configuration object
 * @param {Array} props.data - Data array for the cards
 * @param {Function} props.onCardClick - Card click handler
 * @param {Function} props.actionButtons - Function that returns action buttons JSX
 * @param {Function} props.cardContent - Function that returns card content JSX
 * @param {Function} props.onEdit - Edit handler function
 * @param {Function} props.onDelete - Delete handler function
 * @param {string} props.emptyMessage - Empty state message
 * @param {string} props.editRoute - Base route for edit links
 */
function MobileCardsTemplate({
  data = [],
  onCardClick = () => {},
  actionButtons = null,
  cardContent = null,
  onEdit = null,
  onDelete = null,
  emptyMessage = "No data found",
  editRoute = "/edit"
}) {
  // Default card content template
  const defaultCardContent = (item) => (
    <div className="flex flex-col space-y-3">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-h6 apollo-heading text-primary-600 mb-1">
            {item.title || item.name || `#${item.id}`}
          </h3>
          <p className="text-body-2 apollo-subheading">
            {item.description || item.company_name || item.customer_name || ''}
          </p>
        </div>
        {item.status && (
          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-base font-medium bg-blue-100 text-blue-800">
            {item.status}
          </span>
        )}
      </div>

      {/* Details */}
      <div className="space-y-2">
        {item.created_at && (
          <div className="flex justify-between text-body-2 apollo-body">
            <span>Created:</span>
            <span>{new Date(item.created_at).toLocaleDateString()}</span>
          </div>
        )}
        {item.total && (
          <div className="flex justify-between text-body-2 apollo-subheading text-primary-700 border-t pt-2">
            <span>Total:</span>
            <span>${item.total}</span>
          </div>
        )}
      </div>
    </div>
  );

  // Default action buttons template
  const defaultActionButtons = (item) => (
    <div className="flex gap-2 pt-2" onClick={(e) => e.stopPropagation()}>
      {onEdit && (
        <Link to={`${editRoute}/${item.id}`} className="flex-1">
          <button
            className="w-full inline-flex items-center justify-center px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            Edit
          </button>
        </Link>
      )}
      {onDelete && (
        <button 
          onClick={() => onDelete(item)}
          className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
        >
          Delete
        </button>
      )}
    </div>
  );

  return (
    <div className="block lg:hidden space-y-4 p-4">
      {data && data.length > 0 ? (
        data.map((item) => (
          <Card 
            key={item.id}
            className="cursor-pointer apollo-card hover:shadow-md transition-shadow duration-200"
            style={{ backgroundColor: 'white' }}
            onClick={() => onCardClick(item)}
          >
            {cardContent ? cardContent(item) : defaultCardContent(item)}
            {actionButtons ? actionButtons(item) : defaultActionButtons(item)}
          </Card>
        ))
      ) : (
        <Card className="apollo-card" style={{ backgroundColor: 'white' }}>
          <div className="text-center py-8">
            <p className="text-body-1 apollo-body">{emptyMessage}</p>
          </div>
        </Card>
      )}
    </div>
  );
}

export default MobileCardsTemplate;
